import { GridPaginationModel } from "@mui/x-data-grid";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  IFilterValue,
  TFilterPossibleValues,
} from "../components/Table/model/types";
import {
  getNewEmptySearchParamsFromFilter,
  getNewSearchParams,
  getNewSearchParamsFromFilter,
  getPagination,
  queryFromSearchParams,
} from "../utils/url/query.helpers";
import { useTabs } from "./useTabs";

interface IUseTableSearchParamsProps {
  tabName?: string;
}
export function useTableSearchParams<T>(props?: IUseTableSearchParamsProps) {
  const { tabName } = props || {};
  const [params, setParams] = useSearchParams();
  const tabValue = tabName ? params.get(tabName) : null;

  const onPaginationModelChange = useCallback(
    ({ page, pageSize }: GridPaginationModel) => {
      setParams(getPagination({ page: page + 1, pageSize, params }));
    },
    [setParams, params],
  );

  const onSearch = useCallback(
    (searchStr: string) => {
      setParams((prev) => {
        const prevValue = prev.get("search") ?? "";

        if (prevValue === searchStr) {
          return prev;
        }

        return getNewSearchParams(prev, "search", searchStr);
      });
    },
    [setParams],
  );

  const onResetFilter = useCallback(
    (filtersToReset: Array<string>) => {
      setParams((prev) => {
        const next = getNewEmptySearchParamsFromFilter(prev, filtersToReset);
        if (next.toString() === prev.toString()) {
          return prev;
        }

        return next;
      });
    },
    [setParams],
  );
  const onFilter = useCallback(
    (v: IFilterValue<TFilterPossibleValues | null>[]) => {
      setParams(getNewSearchParamsFromFilter(params, v));
    },
    [params, setParams],
  );

  const { activeTab, setActiveTab } = useTabs(tabValue, (v) =>
    setParams(getNewSearchParams(params, tabName || "", v || "")),
  );

  const searchParams = useMemo(
    () => queryFromSearchParams<T>(params),
    [params],
  );

  return {
    onPaginationModelChange,
    onSearch,
    onFilter,
    onResetFilter,
    paramsObject: searchParams,
    params,
    setParams,
    activeTab: tabName ? activeTab : undefined,
    setActiveTab: tabName ? setActiveTab : undefined,
  };
}
